import { inject, Injectable } from '@angular/core';
import { ApiResponseStats, WagerApi } from '../../api/wager-api';
import { HttpService } from '../http/http.service';
import { JobService } from '../job/job.service';
import { Job } from '../job/job';
import { getMockPlayerStats } from './player-stats.mock';
import { Observable, Subject } from 'rxjs';
import { HashMap, TranslocoService } from '@jsverse/transloco';
import { PlayerStats, PlayerStatsViewModal } from 'src/app/shared/types/stats';
import { ThemeService } from '../theme/theme.service';
import { calcIcon, calcIconColor, calcMedalSource } from 'src/app/shared/utils/stats.utils';
import { CurrencyPipe } from '@angular/common';
import { amountForCurrency } from 'src/app/model/wager/wager';

@Injectable({
  providedIn: 'root',
})
export class PlayerStatsService {
  private httpService = inject(HttpService);
  private jobService = inject(JobService);
  private translocoService = inject(TranslocoService);
  private themeService = inject(ThemeService);
  private currencyPipe = inject(CurrencyPipe);

  private readonly useMockData = false;
  private readonly mockDelay = 1000;
  private statsPlayer = new Subject<string>();
  private statsList: PlayerStats[] = [
    { label: 'best-score', score: 0, percentage: 0 },
    { label: 'avg-score', score: 0, percentage: 0 },
    // { label: 'win-rate', score: 0, percentage: 0 },
    // { label: 'completed-rounds', score: 0, percentage: 0 },
    { label: 'won-rounds', score: 0, percentage: 0 },
    // { label: 'drawn-rounds', score: 0, percentage: 0 },
    { label: 'best-win-streak', score: 0, percentage: 0 },
    { label: 'won-amount', score: 0, percentage: 0 },
  ];
  private pills: { label: string; value: number }[] = [
    { label: 'days', value: 0 },
    // { label: 'matches', value: 0 },
    { label: 'wins', value: 0 },
  ];

  setPlayerStatsModal(playerId: string): void {
    this.statsPlayer.next(playerId);
  }

  getPlayerStatsModal(): Observable<string> {
    return this.statsPlayer.asObservable();
  }

  getInitialPillsData(): { label: string }[] {
    return this.pills.map((item) => ({
      label: `${this.getTranslation(item.label)} ${item.value}`,
    }));
  }

  getInitialStatsListData() {
    return this.transformStatsList(this.statsList);
  }

  formatScore(item: PlayerStats): string {
    if (item.isRate) {
      return `${this.themeService.formatNumber(parseFloat((item.score * 100).toFixed(2)))}%`;
    }
    if (item.isAmount) {
      const currency = this.themeService.currency();
      return amountForCurrency(currency, item.score, this.currencyPipe);
    }

    if (Number.isInteger(item.score)) {
      return this.themeService.formatNumber(item.score);
    } else {
      return this.themeService.formatNumber(parseFloat(item.score.toFixed(2)));
    }
  }

  transformStatsList(data: PlayerStats[]): PlayerStatsViewModal[] {
    return data.map((item) => ({
      ...item,
      label: this.getTranslation(item.label),
      score: this.formatScore(item),
      percentage: this.getTranslation('percentile-rank', { percentile: item.percentage }),
      icon: calcIcon(item.percentage),
      colorCss: calcIconColor(item.percentage),
      medalSource: this.themeService.hasCustomMedal()
        ? calcMedalSource(item.percentage, this.themeService.imagesFolder())
        : undefined,
      currencyIcon: item.isAmount ? this.themeService.currencyIcon() : undefined,
    }));
  }

  getTranslation(key: string, params?: HashMap): string {
    return this.translocoService.translate(`player-stats.${key}`, params);
  }

  getGameStats(userId: string, gameId: string): Job<ApiResponseStats> {
    if (this.useMockData) {
      return this.jobService.create(
        () =>
          new Promise((resolve) => setTimeout(() => resolve(getMockPlayerStats()), this.mockDelay)),
      );
    }

    return this.jobService.create(async () =>
      WagerApi.request(this.httpService).stats(userId, gameId),
    );
  }
}
